import classNames from 'classnames/bind';
import React, { useState, useRef, useEffect } from 'react';

import styles from './Tooltip.module.scss';
import Icon, { IconVariant } from '../Icon';
import RichTextWrapper from '../RichText/RichTextWrapper';
import { TooltipProps, defaultProps } from './TooltipTypes';
import { calculateDirectionBasedonScreenPosition } from './TooltipFunctions';

/**
 * Classnames binding for the Tooltip component.
 * @see [Classnames Usage](https://github.com/JedWatson/classnames#alternate-bind-version-for-css-modules)
 */
const cx = classNames.bind(styles);

/**
 * A Sitecore-ready implementation of the <Tooltip> component.
 *
 * @see [Orchid Storybook](https://upmchporchid.azurewebsites.net/storybook/?path=/story/components-tooltip) for design references
 * @see [Orchid Design System](https://upmchporchid.azurewebsites.net/components/non-skeleton-loading#usage) for usage details
 *
 * @example
 * ```tsx
 * <Tooltip field={value: "Sample content"} />
 * ```
 *
 * @param props - Tooltip properties.
 * @returns A JSX element.
 */
const Tooltip: React.FC<TooltipProps> = (propsWithoutDefaults) => {
  const props = {
    icon: propsWithoutDefaults.icon ?? defaultProps.icon,
    delay: propsWithoutDefaults.delay ?? defaultProps.delay,
    direction: propsWithoutDefaults.direction ?? defaultProps.direction,
    ...propsWithoutDefaults,
  };

  let timeout: NodeJS.Timeout;

  const targetRef = useRef<HTMLSpanElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const { className, icon, field, delay, direction } = props;
  const containerClasses: string = cx(className, 'tooltip-container');

  const [tooltipDirection, setTooltipDirection] = useState(direction?.toString());
  const [active, setActive] = useState(false);

  useEffect(() => {
    const targetElement = targetRef.current;
    const tooltipElement = tooltipRef.current;

    if (active && targetElement && tooltipElement) {
      const newPosition = calculateDirectionBasedonScreenPosition(targetElement, tooltipElement, tooltipDirection);

      if (newPosition) {
        setTooltipDirection(newPosition);
      }
    }

    if (!active && direction !== tooltipDirection) {
      setTooltipDirection(direction);
    }
  }, [tooltipDirection, active, direction]);

  const handleClickOutside = ({ target }: MouseEvent) => {
    assertIsNode(target);
    if (targetRef.current && !targetRef.current.contains(target)) {
      hideTip();
    }
  };

  const toggleTip = () => {
    timeout = setTimeout(() => {
      if (active) {
        hideTip();
      } else {
        setActive(true);
        document.addEventListener('click', handleClickOutside, true);
      }
    }, delay);
  };

  const hideTip = () => {
    timeout && clearInterval(timeout);
    setActive(false);
    document.removeEventListener('click', handleClickOutside, true);
  };

  function assertIsNode(e: EventTarget | null): asserts e is Node {
    if (!e || !('nodeType' in e)) {
      throw new Error(`Node expected`);
    }
  }

  return (
    <span className={containerClasses} ref={targetRef}>
      <button type="button" onClick={toggleTip}>
        <Icon variant={icon as IconVariant} />
      </button>

      <div role="tooltip" ref={tooltipRef} className={cx('tooltip', tooltipDirection)} hidden={!active}>
        <RichTextWrapper field={field} editable={false}></RichTextWrapper>
      </div>
    </span>
  );
};

Tooltip.displayName = 'Tooltip';

export default Tooltip;
